import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/** Chave do identificador deste navegador (localStorage: sobrevive a fechar a aba). */
const CHAVE_DEVICE_ID = 'avatiDeviceId';

/**
 * Sessao do usuario no backend. O sistema permite UMA sessao por plataforma:
 * um navegador e um celular por conta, ao mesmo tempo.
 *
 * O `deviceId` identifica ESTE navegador. Com ele, entrar de novo no mesmo computador reaproveita
 * a propria vaga em vez de esbarrar nela — o bloqueio existe para o segundo navegador, nao para
 * quem ja estava aqui.
 */
@Injectable({ providedIn: 'root' })
export class SessaoService {

  private readonly baseUrl = environment.apiBaseUrl;

  /** Toda sessao aberta pela web ocupa a vaga WEB (o app ocupa a MOBILE). */
  readonly plataforma = 'WEB';

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  /** Identificador deste navegador, criado na primeira vez e reaproveitado depois. */
  obterDeviceId(): string | undefined {
    if (!isPlatformBrowser(this.platformId)) return undefined;

    const existente = localStorage.getItem(CHAVE_DEVICE_ID);
    if (existente) return existente;

    const novo = this.gerarIdentificador();
    localStorage.setItem(CHAVE_DEVICE_ID, novo);
    return novo;
  }

  /**
   * Libera a vaga desta plataforma (logout).
   *
   * Nunca propaga erro: sair do sistema nao pode ficar preso a uma chamada de rede. Se falhar,
   * a vaga e liberada sozinha por inatividade no backend.
   */
  encerrar(): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}/sessao?plataforma=${this.plataforma}`)
      .pipe(catchError(() => of(void 0)));
  }

  /** UUID v4 simples — `crypto.randomUUID` nao existe em navegador antigo nem em contexto inseguro. */
  private gerarIdentificador(): string {
    const cripto = (globalThis as any)?.crypto;
    if (cripto?.randomUUID) return cripto.randomUUID();
    return 'web-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}
